import { Component, Inject, OnInit, ViewChild, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { IContractTemplate } from 'src/app/domain/registration/models/IContractTemplate';
import { AddTemplateDialogComponent } from 'src/app/shared/components/add-template-dialog/add-template-dialog.component';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { PartnerService } from '../../../crud-service';
import { ITemplateVariables } from 'src/app/domain/registration/models/ITemplateVariables';
import { PartnerContractService } from '../../../services/partner-contract.service';
import { TemplatePreviewComponent } from 'src/app/shared/components/template-preview/template-preview.component';
import { trustResourceUrl } from 'src/app/shared/utils/safe-html.util';

@Component({
    selector: 'app-contract-template-dialog',
    templateUrl: './contract-template-dialog.component.html',
    styleUrls: ['./contract-template-dialog.component.sass'],
})

export class ContractTemplateDialogComponent implements OnInit {
    @ViewChild('consultantFileInput') consultantFileInput!: ElementRef;
    @ViewChild('representativeFileInput') representativeFileInput!: ElementRef;
    @ViewChild('counterFileInput') counterFileInput!: ElementRef;
    @ViewChild('partnerFileInput') partnerFileInput!: ElementRef;
    @ViewChild('unitFileInput') unitFileInput!: ElementRef;

    form: FormGroup;
    selectedFile: File | null = null;
    currentTemplate: string = '';
    currentType: string = '';
    loading = false;
    activeTemplateType = 'partner'; // Começa mostrando o template de parceiro

    // Variáveis de template para o HTML
    templateVariables: ITemplateVariables = {
        nome_parceiro: '{nome_parceiro}',
        documento_parceiro: '{documento_parceiro}',
        email_parceiro: '{email_parceiro}',
        telefone_parceiro: '{telefone_parceiro}',
        endereco_parceiro: '{endereco_parceiro}',
        sistema_parceiro: '{sistema_parceiro}',
        tipo_sistema: '{tipo_sistema}',
        email_sistema: '{email_sistema}',
        descricao_sistema: '{descricao_sistema}',
        data_inicio: '{data_inicio}',
        data_termino: '{data_termino}',
        vencimento_sistema: '{vencimento_sistema}'
    };

    // Templates por tipo de parceiro

    templateCategories = [
        {
            type: 'partner',
            title: 'Parceiro',
            formControlName: 'partnerTemplate',
            icon: 'handshake',
            description: 'Template para contratos com parceiros',
        },
        {
            type: 'consultant',
            title: 'Consultor',
            formControlName: 'consultantTemplate',
            icon: 'person',
            description: 'Template para contratos com consultores',
        },
        {
            type: 'representative',
            title: 'Representante',
            formControlName: 'representativeTemplate',
            icon: 'groups',
            description: 'Template para contratos com representantes',
        },
        {
            type: 'counter',
            title: 'Contador',
            formControlName: 'counterTemplate',
            icon: 'calculate',
            description: 'Template para contratos com contadores',
        },
        {
            type: 'unit',
            title: 'Unidade',
            formControlName: 'unitTemplate',
            icon: 'business',
            description: 'Template para contratos com unidades',
        }
    ];

    // Armazenar informações completas dos templates
    templates: Record<string, IContractTemplate> = {};
    previewUrls: Record<string, SafeResourceUrl> = {};
    // Armazenar arquivos selecionados pelo usuário para cada tipo
    selectedFiles: Record<string, File | null> = {};

    constructor(
        private fb: FormBuilder,
        private dialogRef: MatDialogRef<ContractTemplateDialogComponent>,
        private dialog: MatDialog,
        private partnerService: PartnerService,
        private partnerContractService: PartnerContractService,
        private toastService: ToastService,
        private sanitizer: DomSanitizer,
        @Inject(MAT_DIALOG_DATA) public data: any
    ) {
        // Inicializa o formulário com um campo para cada tipo de template
        const formControls: Record<string, any> = {};
        this.templateCategories.forEach(category => {
            formControls[category.formControlName] = [''];
        });
        this.form = this.fb.group(formControls);
    }

    ngOnInit() {
        this.loadTemplates();
    }

    loadTemplates() {
        this.loading = true;
        this.partnerContractService.getPartnerContractTemplates().subscribe({
            next: (templatesData: Record<string, IContractTemplate>) => {
                console.log('Templates recebidos da API:', templatesData);
                this.templates = templatesData;

                const formValues: Record<string, string> = {};

                Object.keys(templatesData).forEach(key => {
                    const template = templatesData[key];
                    if (template && template.content) {
                        let content = template.content;
                        content = content.replace(/\{\{([^}]+)\}\}/g, '{$1}');

                        formValues[key] = content;

                        if (template.fileUrl) {
                            this.previewUrls[key] = trustResourceUrl(this.sanitizer, template.fileUrl);
                        }
                    }
                });

                this.form.patchValue(formValues);
                this.loading = false;
            },
            error: (error: any) => {
                console.error('Erro ao carregar templates de parceiros:', error);
                this.toastService.showError('Erro ao carregar os templates. Tente novamente.');
                this.loading = false;

                // Fallback para o método antigo se o novo falhar
                this.loadTemplatesFallback();
            }
        });
    }

    loadTemplatesFallback() {
        console.log('Carregando templates pelo método fallback');
        this.partnerService.getContractTemplate().subscribe({
            next: (templatesData: Record<string, IContractTemplate>) => {
                this.templates = templatesData;

                // Atualizar o formulário com os dados dos templates
                const formValues: Record<string, string> = {};

                Object.keys(templatesData).forEach(key => {
                    const template = templatesData[key];
                    if (template && template.content) {
                        // Convertendo variáveis de {{var}} para {var} se necessário
                        let content = template.content;
                        content = content.replace(/\{\{([^}]+)\}\}/g, '{$1}');

                        formValues[key] = content;

                        // Criar URL segura para visualização se houver file_url
                        if (template.fileUrl) {
                            this.previewUrls[key] = trustResourceUrl(this.sanitizer, template.fileUrl);
                        }
                    }
                });

                this.form.patchValue(formValues);
                this.loading = false;
            },
            error: (error: any) => {
                console.error('Erro ao carregar templates pelo método fallback:', error);
                this.toastService.showError('Erro ao carregar os templates. Tente novamente.');
                this.loading = false;
            }
        });
    }

    onFileSelected(event: any, type: string) {
        const file = event.target.files[0];
        if (!file) {
            return;
        }

        this.selectedFile = file;
        this.currentType = type;

        // Armazenar o arquivo selecionado associado ao tipo de template
        this.selectedFiles[type] = file;

        // Verificar o tipo do arquivo
        const isHtml = file.name.toLowerCase().endsWith('.html') || file.name.toLowerCase().endsWith('.htm');

        if (isHtml) {
            // Para arquivos HTML, processar para exibir no editor e atualizar metadados
            this.processHtmlFile(file, type);
        } else {
            // Para outros tipos (como DOCX), apenas armazenamos o arquivo.
            // Atualizar a UI para indicar que um arquivo foi selecionado
            // Poderíamos atualizar 'templates[type]' com metadados básicos se necessário
            if (!this.templates[type]) {
                this.templates[type] = {} as IContractTemplate; // Initialize if not exists
            }
            this.templates[type].fileName = file.name;
            this.templates[type].fileType = file.type; // Store MIME type
            this.templates[type].content = ''; // Clear content if a non-HTML file is uploaded
            this.form.get(type)?.setValue(''); // Clear editor content

            this.toastService.showInfo(`Arquivo ${file.name} selecionado. Clique em 'Salvar Templates' para confirmar.`);
            // Limpar previewUrl se existir, pois não é mais um HTML/URL direto
            delete this.previewUrls[type];
        }

        // Limpar o input de arquivo para permitir selecionar o mesmo arquivo novamente
        if (event.target) {
            event.target.value = null;
        }
    }

    processHtmlFile(file: File, type: string) {
        const reader = new FileReader();
        reader.onload = (e) => {
            this.currentTemplate = e.target?.result as string;

            // Convertendo variáveis de {{var}} para {var} se necessário
            let content = this.currentTemplate;
            content = content.replace(/\{\{([^}]+)\}\}/g, '{$1}');

            this.form.get(type)?.setValue(content);

            // Criar um template temporário para armazenar os metadados do arquivo
            if (!this.templates[type]) {
                this.templates[type] = {
                    name: `Template ${this.getTemplateCategoryTitle(type)}`,
                    content: content,
                    type: this.getTemplateType(type) as any,
                    fileName: file.name,
                    fileType: 'html'
                };
            } else {
                this.templates[type].content = content;
                this.templates[type].fileName = file.name;
                this.templates[type].fileType = 'html';
            }

            // Preview automático após upload
            this.toastService.showInfo(`Arquivo HTML ${file.name} carregado. Clique em 'Salvar Templates' para confirmar.`);
        };
        reader.readAsText(file);
    }

    openTemplateEditor(type: string) {
        const category = this.templateCategories.find(c => c.formControlName === type);
        if (!category) return;

        const dialogRef = this.dialog.open(AddTemplateDialogComponent, {
            width: '900px',
            maxHeight: '90vh',
            data: {
                template: this.form.get(type)?.value || '',
                type: category.type,
                templateTitle: category.title
            },
            panelClass: 'template-editor-dialog'
        });

        dialogRef.afterClosed().subscribe((result: any) => {
            if (result) {
                // Se o resultado for um objeto, pega o conteúdo
                let content = typeof result === 'object' ? result.content : result;

                // Convertendo variáveis de {{var}} para {var} se necessário
                content = content.replace(/\{\{([^}]+)\}\}/g, '{$1}');

                this.form.get(type)?.setValue(content);

                // Atualizar o template
                if (!this.templates[type]) {
                    this.templates[type] = {
                        name: `Template ${category.title}`,
                        content: content,
                        type: category.type as any,
                        fileType: 'html'
                    };
                } else {
                    this.templates[type].content = content;
                    // Se foi editado manualmente, consideramos como HTML e limpamos o arquivo selecionado
                    this.templates[type].fileType = 'html';
                    this.selectedFiles[type] = null; // Clear selected file if manually edited
                    delete this.templates[type].fileUrl; // Clear file URL if edited
                }
            }
        });
    }

    previewTemplate(type: string) {
        const template = this.templates[type];
        const content = this.form.get(type)?.value;
        const category = this.templateCategories.find(c => c.formControlName === type);

        if (!content) {
            this.toastService.showInfo('Não há template salvo para visualização.');
            return;
        }

        // Se for um PDF ou um arquivo hospedado, abrir em uma nova aba
        if (template && template.fileUrl && template.fileType === 'pdf') {
            window.open(template.fileUrl, '_blank');
            return;
        }

        // Para HTML, mostrar uma prévia formatada
        const previewData = {
            title: `Prévia do Template de ${category?.title || 'Contrato'}`,
            content: this.processTemplateForPreview(content),
            variables: this.getPreviewVariables(),
            previewMode: true
        };

        // Abrir o componente de prévia
        this.dialog.open(TemplatePreviewComponent, {
            width: '900px',
            maxHeight: '90vh',
            data: previewData,
            panelClass: 'template-preview-dialog'
        });
    }

    processTemplateForPreview(template: string): string {
        let processedTemplate = template;

        // Substituir todas as variáveis por valores de exemplo
        const previewVariables = this.getPreviewVariables();

        Object.keys(previewVariables).forEach(key => {
            const regex = new RegExp(`{${key}}`, 'g');
            processedTemplate = processedTemplate.replace(regex, previewVariables[key]);
        });

        return processedTemplate;
    }

    getPreviewVariables(): Record<string, string> {
        return {
            nome_parceiro: 'Empresa Exemplo LTDA',
            documento_parceiro: '12.345.678/0001-90',
            email_parceiro: 'contato@empresaexemplo.com.br',
            telefone_parceiro: '(11) 9876-5432',
            endereco_parceiro: 'Rua Exemplo, 123, São Paulo/SP',
            sistema_parceiro: 'Sistema de Gestão',
            tipo_sistema: 'Principal',
            email_sistema: 'sistema@grupomegatech.com.br',
            descricao_sistema: 'Sistema para gestão completa da empresa',
            data_inicio: '01/01/2023',
            data_termino: '31/12/2023',
            vencimento_sistema: 'Dia 10',
            data_atual: new Date().toLocaleDateString('pt-BR'),
            mes_atual: this.getNomeDoMes(new Date().getMonth()),
            dia_atual: new Date().getDate().toString(),
            ano_atual: new Date().getFullYear().toString()
        };
    }

    getNomeDoMes(mes: number): string {
        const meses = [
            'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
            'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
        ];
        return meses[mes];
    }

    downloadTemplate(type: string) {
        const category = this.templateCategories.find(c => c.formControlName === type);
        const template = this.templates[type];
        if (template && template.fileUrl) {
            const link = document.createElement('a');
            link.href = template.fileUrl || '';
            link.download = template.fileName || `template_${category?.type || type}.html`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } else {
            // Se não tiver URL mas tiver conteúdo, gerar um arquivo para download
            const content = this.form.get(type)?.value;
            if (content) {
                const blob = new Blob([content], { type: 'text/html' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `template_${category?.type || type}.html`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            } else {
                this.toastService.showInfo('Não há template salvo para download.');
            }
        }
    }

    async onSave() {
        // Não verificar form.valid, pois o conteúdo pode vir de um arquivo não-HTML
        // if (this.form.valid) { <-- Removido
        this.loading = true;
        const savePromises: Promise<any>[] = [];
        let templatesSavedCount = 0;

        try {
            // Iterar sobre cada categoria de template
            for (const category of this.templateCategories) {
                const formControlName = category.formControlName;
                const templateType = category.type;
                const selectedFile = this.selectedFiles[formControlName];
                const htmlContentFromForm = this.form.get(formControlName)?.value;

                // Priorizar o arquivo selecionado pelo usuário
                if (selectedFile) {
                    console.log(`💾 Salvando template '${category.title}' usando arquivo selecionado: ${selectedFile.name}`);
                    const formData = new FormData();
                    formData.append('file', selectedFile, selectedFile.name); // Enviar o arquivo original
                    formData.append('name', `Template ${category.title}`);
                    formData.append('type', templateType);
                    // O backend determinará o conteúdo se for HTML ou o manterá nulo para DOCX/outros

                    savePromises.push(
                        this.partnerContractService.savePartnerContractTemplate(formData).toPromise()
                    );
                    templatesSavedCount++;
                }
                // Se não houver arquivo selecionado, mas houver conteúdo no editor HTML
                else if (htmlContentFromForm && typeof htmlContentFromForm === 'string' && htmlContentFromForm.trim().length > 0) {
                    console.log(`💾 Salvando template '${category.title}' usando conteúdo do editor HTML.`);
                    // Criar HTML completo
                    const fullHtmlContent = `<!DOCTYPE html>
                        <html>
                        <head>
                            <meta charset="UTF-8">
                            <title>Template ${category.title}</title>
                            <style>/* Estilos CSS aqui */</style>
                        </head>
                        <body>
                            ${htmlContentFromForm}
                        </body>
                        </html>`;
                    const formData = new FormData();
                    const blob = new Blob([fullHtmlContent], { type: 'text/html' });
                    // Usar um nome de arquivo padrão, pois não veio de um upload
                    const file = new File([blob], `template_${templateType}_edited.html`, { type: 'text/html' });

                    formData.append('file', file);
                    formData.append('name', `Template ${category.title}`);
                    formData.append('type', templateType);
                    // Incluir o conteúdo explicitamente pode ser redundante se o backend lê do arquivo,
                    // mas pode ser útil dependendo da lógica do backend.
                    // formData.append('content', fullHtmlContent);

                    savePromises.push(
                        this.partnerContractService.savePartnerContractTemplate(formData).toPromise()
                    );
                    templatesSavedCount++;
                } else {
                    console.log(`ℹ️ Nenhum arquivo selecionado ou conteúdo no editor para o template '${category.title}'. Pulando.`);
                }
            }

            // Esperar todas as promessas de salvamento terminarem
            if (savePromises.length > 0) {
                await Promise.all(savePromises);
                this.toastService.showSuccess(`${templatesSavedCount} template(s) salvo(s) com sucesso!`);
                // Limpar arquivos selecionados após salvar
                this.selectedFiles = {};
                // Recarregar os templates após salvar para refletir o estado atual
                this.loadTemplates();
                this.dialogRef.close(true); // Fechar diálogo apenas se algo foi salvo
            } else {
                this.toastService.showInfo('Nenhuma alteração nos templates para salvar.');
            }

        } catch (error: any) {
            console.error('Erro ao salvar templates de parceiros:', error);
            this.toastService.showError('Erro ao salvar um ou mais templates. Tente novamente.');
        } finally {
            this.loading = false;
        }
    }

    hasTemplate(type: string): boolean {
        return !!this.templates[type]?.fileUrl || !!this.form.get(type)?.value;
    }

    getTemplateName(type: string): string {
        const category = this.templateCategories.find(c => c.formControlName === type);
        // Se um arquivo foi selecionado, usar o nome dele
        if (this.selectedFiles[type]) {
            return this.selectedFiles[type]?.name || category?.title || '';
        }
        // Senão, usar o nome do template carregado ou o título da categoria
        return this.templates[type]?.name || category?.title || '';
    }

    getTemplateFileType(type: string): string {
        // Se um arquivo foi selecionado, usar o tipo dele
        if (this.selectedFiles[type]) {
            const file = this.selectedFiles[type];
            if (file?.type) return file.type; // MIME Type
            // Fallback para extensão se MIME type não estiver disponível
            const extension = file?.name.split('.').pop()?.toUpperCase();
            return extension || '';
        }
        // Senão, usar o tipo do template carregado (se houver)
        const template = this.templates[type];
        if (template?.fileType) {
            // Se for HTML, mostrar HTML. Se for application/vnd..., mostrar DOCX.
            if (template.fileType.includes('html')) return 'HTML';
            if (template.fileType.includes('officedocument.word')) return 'DOCX';
            return template.fileType.toUpperCase(); // Outros tipos
        }
        // Se não houver arquivo selecionado nem template carregado com tipo, retornar vazio
        return '';
    }

    getTemplateCategoryTitle(formControlName: string): string {
        const category = this.templateCategories.find(c => c.formControlName === formControlName);
        return category?.title || '';
    }

    getTemplateType(formControlName: string): string {
        const category = this.templateCategories.find(c => c.formControlName === formControlName);
        return category?.type || '';
    }

    onCancel() {
        this.dialogRef.close();
    }

    // Método para alterar o tipo de template ativo
    changeTemplateType(type: string) {
        this.activeTemplateType = type;
    }

    // Método para obter o formControlName a partir do tipo
    getFormControlNameByType(type: string): string {
        const category = this.templateCategories.find(c => c.type === type);
        return category ? category.formControlName : '';
    }

    // Método para verificar se o tipo de template está ativo
    isTemplateTypeActive(type: string): boolean {
        return this.activeTemplateType === type;
    }

    /**
     * Verifica se o template ativo é o primeiro da lista
     */
    isFirstTemplate(): boolean {
        const currentIndex = this.getCurrentTemplateIndex();
        return currentIndex === 0;
    }

    /**
     * Verifica se o template ativo é o último da lista
     */
    isLastTemplate(): boolean {
        const currentIndex = this.getCurrentTemplateIndex();
        return currentIndex === this.templateCategories.length - 1;
    }

    /**
     * Navega para o template anterior
     */
    navigateToPrevTemplate(): void {
        const currentIndex = this.getCurrentTemplateIndex();
        if (currentIndex > 0) {
            this.activeTemplateType = this.templateCategories[currentIndex - 1].type;
        }
    }

    /**
     * Navega para o próximo template
     */
    navigateToNextTemplate(): void {
        const currentIndex = this.getCurrentTemplateIndex();
        if (currentIndex < this.templateCategories.length - 1) {
            this.activeTemplateType = this.templateCategories[currentIndex + 1].type;
        }
    }

    /**
     * Obtém o índice do template ativo
     */
    private getCurrentTemplateIndex(): number {
        return this.templateCategories.findIndex(c => c.type === this.activeTemplateType);
    }

    // Método para lidar com o clique nos inputs de arquivo
    triggerFileInput(type: string, fileType: string = 'html'): void {
        // Atualizar o tipo de arquivo aceito antes de abrir o seletor de arquivo
        let fileInput: ElementRef | null = null;

        switch (type) {
            case 'consultantTemplate':
                fileInput = this.consultantFileInput;
                break;
            case 'representativeTemplate':
                fileInput = this.representativeFileInput;
                break;
            case 'counterTemplate':
                fileInput = this.counterFileInput;
                break;
            case 'partnerTemplate':
                fileInput = this.partnerFileInput;
                break;
            case 'unitTemplate':
                fileInput = this.unitFileInput;
                break;
        }

        if (fileInput && fileInput.nativeElement) {
            // Ajustar o tipo de arquivo aceito para HTML ou DOCX
            fileInput.nativeElement.accept = '.html,.htm,.docx'; // Aceita ambos

            fileInput.nativeElement.click();
        }
    }
} 
